/**
 * Browser-only memorial photo enhancement settings (preview filters — original image untouched).
 */

export const DEFAULT_IMAGE_EDITS = {
  filter: 'none',
  brightness: 0,
  contrast: 0,
  sharpen: 0,
  rotation: 0,
};

export function normalizeImageEdits(edits) {
  if (!edits || typeof edits !== 'object') return { ...DEFAULT_IMAGE_EDITS };
  const rotation = Number(edits.rotation) || 0;
  const normalizedRotation = [0, 90, 180, 270].includes(rotation) ? rotation : 0;
  const filter = ['none', 'black_white', 'sepia'].includes(edits.filter) ? edits.filter : 'none';
  return {
    filter,
    brightness: clampNumber(edits.brightness, -50, 50, 0),
    contrast: clampNumber(edits.contrast, -50, 50, 0),
    sharpen: edits.sharpen ? 1 : 0,
    rotation: normalizedRotation,
  };
}

function clampNumber(value, min, max, fallback) {
  const n = Number(value);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function buildPhotoEditFilter(edits) {
  const normalized = normalizeImageEdits(edits);
  const parts = [];

  if (normalized.filter === 'black_white') {
    parts.push('grayscale(100%)');
  } else if (normalized.filter === 'sepia') {
    parts.push('sepia(55%)', 'saturate(1.15)', 'hue-rotate(-8deg)');
  }

  const brightness = 1 + normalized.brightness / 100;
  const contrast = 1 + normalized.contrast / 100;
  parts.push(`brightness(${brightness.toFixed(3)})`);
  parts.push(`contrast(${contrast.toFixed(3)})`);

  if (normalized.sharpen > 0) {
    parts.push('contrast(1.08)', 'saturate(1.04)');
  }

  return parts.length ? parts.join(' ') : 'none';
}

export function rotateImageEdits(edits, direction) {
  const normalized = normalizeImageEdits(edits);
  const delta = direction === 'left' ? -90 : 90;
  let next = normalized.rotation + delta;
  while (next < 0) next += 360;
  next %= 360;
  return { ...normalized, rotation: next };
}

export function hasActiveImageEdits(edits) {
  const normalized = normalizeImageEdits(edits);
  return (
    normalized.filter !== 'none' ||
    normalized.brightness !== 0 ||
    normalized.contrast !== 0 ||
    normalized.sharpen !== 0 ||
    normalized.rotation !== 0
  );
}

export function getImageEditStyle(edits, imagePosition) {
  const normalized = normalizeImageEdits(edits);
  const pos = imagePosition || {};
  const zoom = pos.zoom ?? 1;
  const x = pos.x ?? 50;
  const y = pos.y ?? 50;
  const filter = buildPhotoEditFilter(normalized);

  return {
    filter: filter === 'none' ? undefined : filter,
    objectPosition: `${x}% ${y}%`,
    transform: `rotate(${normalized.rotation}deg) scale(${zoom})`,
    transformOrigin: `${x}% ${y}%`,
  };
}
